import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router'
import { checkTenantMismatch } from '@/composables/checkTenantMismatch'
import { shouldBounceSetupToLogin } from '@/composables/shouldBounceSetupToLogin'
import { shouldRequirePasskey } from '@/composables/shouldRequirePasskey'
import { shouldBlockForSubscription } from '@/composables/subscriptionGate'
import { resolveTenantNotFoundRedirect } from '@/composables/tenantGuard'
import { isCurrentAdminStale, isOps, useCurrentAdmin } from '@/composables/useCurrentAdmin'
import { useFeatures } from '@/composables/useFeatures'
import { resetSearchContext } from '@/composables/useGlobalSearch'
import {
  shouldBounceDashboardToSetup,
  shouldBounceSetupRoute,
  useOnboardingResume,
} from '@/composables/useOnboardingResume'
import { useTenant } from '@/composables/useTenant'
import { shouldBounceToTermsAccept, useTermsGate } from '@/composables/useTermsGate'
import { liveMeetingGuard } from '@/router/liveMeetingGuard'
import { hashScrollBehavior } from '@/router/scrollBehavior'
import { api } from '@/utils/apiClient'
import { applyCanonical } from '@/utils/canonicalUrl'
import { clearChunkRecoveryState, handleChunkLoadError } from '@/utils/chunkRecovery'
import { endOperatorSession } from '@/utils/endOperatorSession'
import { landingRouteMeta } from '@/utils/landingPageMeta'
import { isMarketingHost, PLATFORM_HOST } from '@/utils/platformHost'
import { applyRobotsMeta } from '@/utils/robotsMeta'
import { createRouteMetadataUpdater } from '@/utils/routeMetadata'

// Detect context from hostname (see utils/platformHost).
// Marketing = the apex (stemne.no, qa.stemne.no, or bare localhost).
// Tenant = {slug}.<apex>.
//
// Local dev:
//   http://localhost:5173            → marketing (apex equivalent)
//   http://kodelauget.localhost:5173 → tenant app
const isMarketing = isMarketingHost()

// Booking flow.
//
// On tenant subdomains the five steps share a BookingLayout so the
// outer Shell (header + footer-with-garden) stays mounted across step
// navigation — without this, every transition replayed the flora-enter
// animation in the footer (issue #150). Per-step content width comes
// The databehandleravtale page, registered in BOTH contexts — unlike /privacy
// and /terms, which are marketing-only because anonymous visitors only meet
// them on stemne.no.
//
// This one is different: DpaAcceptView is a blocking screen shown on a TENANT
// subdomain, and it links to the agreement. A marketing-only route means that
// link 404s exactly where it is load-bearing — an operator asked to accept a
// document they cannot open. Public (no auth) on purpose: a blocked operator
// has to read it before ticking the box, and their own DPO may need it too.
const dataProcessingAgreementRoute: RouteRecordRaw = {
  path: '/data-processing-agreement',
  name: 'data-processing-agreement',
  component: () => import('@/views/marketing/DataProcessingAgreementView.vue'),
}

// The «Betaling ved booking» annex, registered in BOTH contexts for the same
// reason as the databehandleravtale above.
//
// The operator meets this page on their OWN subdomain: from the add-on card
// in Abonnement → Tillegg when they buy the add-on, and from the Betalinger
// tab when they connect Stripe. Both are tenant-context surfaces, so a
// marketing-only route would 404 at exactly the two moments the document is
// load-bearing — the moment they take on being the seller of record, and the
// moment refunds, disputes and payouts start to concern them. It has to exist
// on the apex too, because /terms links to it from the refund section.
// Public (no auth) on purpose: it is an annex to the public Brukervilkår.
const paymentTermsRoute: RouteRecordRaw = {
  path: '/payment-terms',
  name: 'payment-terms',
  component: () => import('@/views/marketing/PaymentTermsView.vue'),
}

// The operatøravtale (#3025 P1), registered in BOTH contexts for the same
// reason as the databehandleravtale above: the blocking accept screen runs on
// a TENANT subdomain and links here, so a marketing-only route would 404 at
// exactly the moment the document is load-bearing — an operator asked to
// legally accept a contract they cannot open. Public (no auth) on purpose: a
// blocked operator has to read it before ticking the box, and their own
// adviser may need it too.
//
// The English path matches /data-processing-agreement and /payment-terms; the
// surrounding UI is bilingual, so a Norwegian-only URL reads as a bug next to
// English ones.
const operatorAgreementRoute: RouteRecordRaw = {
  path: '/operator-agreement',
  name: 'operator-agreement',
  component: () => import('@/views/marketing/OperatorAgreementView.vue'),
}

// from route meta `maxW`.
const bookingRoutes: RouteRecordRaw[] = isMarketing
  ? [
      {
        path: '/',
        name: 'landing',
        component: () => import('@/views/marketing/LandingView.vue'),
      },
      {
        path: '/for/radgivere',
        name: 'advisors-landing',
        component: () => import('@/views/marketing/AdvisorsLandingView.vue'),
        meta: landingRouteMeta('/for/radgivere'),
      },
      {
        path: '/for/pt',
        name: 'pt-landing',
        component: () => import('@/views/marketing/PtLandingView.vue'),
        meta: landingRouteMeta('/for/pt'),
      },
      // Legal pages — needed as public URLs by Google's OAuth
      // consent screen (which won't approve the calendar-sync
      // client without privacy + terms links). Stay on the
      // marketing context only; tenant subdomains don't need
      // their own copies since the operator/customer flows
      // happen under contract, not as anonymous visits.
      {
        path: '/privacy',
        name: 'privacy',
        component: () => import('@/views/marketing/PrivacyView.vue'),
      },
      {
        path: '/terms',
        name: 'terms',
        component: () => import('@/views/marketing/TermsView.vue'),
      },
      {
        path: '/faq',
        name: 'faq',
        component: () => import('@/views/marketing/FaqView.vue'),
      },
      dataProcessingAgreementRoute,
      paymentTermsRoute,
      operatorAgreementRoute,
    ]
  : [
      dataProcessingAgreementRoute,
      paymentTermsRoute,
      operatorAgreementRoute,
      {
        path: '/',
        component: () => import('@/views/booking/BookingLayout.vue'),
        children: [
          {
            path: '',
            name: 'services',
            component: () => import('@/views/booking/ServiceLandingView.vue'),
          },
          {
            // `/book` is the entry point the rebook deeplink names — the
            // pending-expiry email has shipped `{baseURL}/book?rebook_token=…`
            // since #171. There has never been a route at that exact path
            // (only `book/time`, `book/details`, …), so it fell to the
            // catch-all 404 and the token was silently discarded. Redirect to
            // the service picker with the query intact, where BookingLayout
            // reads the token.
            path: 'book',
            redirect: (to) => ({ path: '/', query: to.query }),
          },
          {
            path: 'book/person/:id',
            name: 'person-landing',
            component: () => import('@/views/booking/PersonLandingView.vue'),
            props: true,
          },
          {
            // 'choose' mode (#564 follow-up): customer lands here
            // after picking a service and chooses a person or
            // "spiller ingen rolle" (= pooled). The route is mounted
            // unconditionally; the view itself bounces back to /book/time
            // when the operator's mode is pooled or pick-first, so a
            // direct link never breaks even if the operator toggles
            // the mode mid-session.
            path: 'book/who',
            name: 'choose-person',
            component: () => import('@/views/booking/ChoosePersonView.vue'),
          },
          {
            path: 'book/time',
            name: 'pick-time',
            component: () => import('@/views/booking/PickTimeView.vue'),
            // #1427: a customer landing on /book/time without picking
            // a service (direct URL nav, stale link) would otherwise
            // pass the empty service through to POST /api/bookings and
            // persist a service_id=NULL "0 min" booking. Bounce to the
            // service picker so they re-enter the flow at step 1. The
            // backend's createBookingRequest validation is the safety
            // net; this is the UX-friendly first line.
            beforeEnter: async () => {
              const { useBookingFlow } = await import('@/composables/useBookingFlow')
              if (!useBookingFlow().state.selectedService) {
                return { path: '/' }
              }
              return true
            },
          },
          {
            path: 'book/details',
            name: 'details',
            meta: { maxW: 720 },
            component: () => import('@/views/booking/DetailsView.vue'),
          },
          {
            path: 'book/confirm',
            name: 'confirm',
            meta: { maxW: 720 },
            component: () => import('@/views/booking/ConfirmView.vue'),
          },
          {
            path: 'confirmed',
            name: 'confirmed',
            meta: { maxW: 720 },
            component: () => import('@/views/booking/ConfirmedView.vue'),
          },
        ],
      },
    ]

// Angreskjema (#3043) — the statutory withdrawal form, at the operator's own
// host so the identity on it is theirs.
//
// Tenant context ONLY: the form names a TRADER, and on the apex there is no
// trader to name. A stemne.no/angreskjema would render an empty government
// form, which is worse than a 404.
//
// Public and unauthenticated on purpose: a consumer exercising the right of
// withdrawal may have no booking left to authenticate with, and § 8 (1) h
// requires the form to be available BEFORE the contract exists at all.
const angrerettRoutes: RouteRecordRaw[] = isMarketing
  ? []
  : [
      {
        path: '/angreskjema',
        name: 'angreskjema',
        component: () => import('@/views/booking/AngreskjemaView.vue'),
      },
    ]

// Customer portal (magic-token gated)
const portalRoutes: RouteRecordRaw[] = [
  {
    path: '/portal',
    name: 'portal',
    component: () => import('@/views/portal/PortalView.vue'),
  },
  {
    // Portal-native reschedule (#2342). Sibling of /booking/reschedule but
    // sourced from the portal session cookie by booking id instead of a
    // magic token — the portal list DTO never carries a manage token. The
    // view bootstraps the portal session on mount and redirects a direct hit
    // without a live session (or an unknown/unmanageable id) back to /portal.
    path: '/portal/reschedule/:id',
    name: 'portal-reschedule',
    component: () => import('@/views/portal/PortalRescheduleView.vue'),
  },
  {
    // Customer room view (S5 commit 3). Renders the document timeline
    // + chat thread shared with the operator. Token comes via ?token=
    // query — the same multi-use customer-access token /portal uses,
    // so a customer who landed on /portal can navigate here without
    // re-redeeming. Gated server-side by features.Require so /api
    // calls 404 when rooms.shared-room is off — the view degrades
    // open and shows an "ikke tilgjengelig" placeholder in that case.
    path: '/rom',
    name: 'rom',
    component: () => import('@/views/portal/RoomView.vue'),
  },
  {
    // Additional meeting invitee's own page (#meeting-invites). The token
    // is the invitee's per-booking token from their invite email — distinct
    // from the primary customer's manage token. Read-only meeting view +
    // the documents the operator shared with all participants.
    path: '/meeting',
    name: 'invitee-meeting',
    component: () => import('@/views/portal/InviteeView.vue'),
  },
  {
    // Time-poll voting page (booking.time-poll). The token is the voter's
    // own per-poll magic token from their poll-invite email. The voter marks
    // each candidate time, watches the live tally, and (for team voters)
    // confirms/declines the RSVP. Gated server-side by the feature flag, so
    // the /api/poll calls fail when off and the view shows the not-found state.
    path: '/poll',
    name: 'poll-vote',
    component: () => import('@/views/portal/PollVoteView.vue'),
  },
  {
    // The customer's pay page for money owed AFTER the meeting (#3063). The
    // reminder emails link to {tenant base}/betal/{token} — the operator's own
    // frontend host — and this is what receives it. The token in the path is
    // the credential; there is no session and no login, exactly like the
    // manage link below.
    //
    // Also the RETURN address: the backend hands Stripe this same URL as both
    // the success (`?status=betalt`) and cancel target, because the booking's
    // magic token is not reachable from the pay path and this is the one
    // address the customer definitely holds.
    //
    // No client-side feature gate. features.Require 404s the endpoint when
    // payments.post-service is off, which the view renders as a retryable
    // failure — gating the ROUTE would mean deciding before the boot flags
    // land and flashing a wrong screen at a customer holding a live link.
    path: '/betal/:token',
    name: 'pay-request',
    component: () => import('@/views/portal/PayRequestView.vue'),
  },
  {
    path: '/booking',
    name: 'manage',
    // /booking/manage is the path the verification email links to
    // ({baseURL}/booking/manage?token=...&action=confirm). Add it as an
    // alias so customers clicking the email don't land on a 404 / blank
    // page. The ManageView reads ?action=confirm and POSTs the
    // confirmation before rendering.
    alias: '/booking/manage',
    component: () => import('@/views/portal/ManageView.vue'),
  },
  {
    // Customer-side landing for "your operator changed your email,
    // click to confirm". Token rides in the query string; the view
    // posts to /api/public/customer/email-confirm on mount.
    path: '/customer/confirm-email',
    name: 'customer-email-confirm',
    component: () => import('@/views/customer/EmailConfirmView.vue'),
  },
  {
    // #631: old Norwegian path. Emails already in flight (and any
    // bookmarks) still point here — redirect until they've expired.
    path: '/kunde/bekreft-epost',
    redirect: (to) => ({ name: 'customer-email-confirm', query: to.query }),
  },
  {
    // Outside-owner "mark done" landing (S8c, #2130). ?id=&token= ride in
    // the query; the view POSTs on the button tap (no mutation on load).
    path: '/oppfolging',
    name: 'external-done',
    component: () => import('@/views/portal/OppfolgingConfirmView.vue'),
  },
  {
    path: '/booking/reschedule',
    name: 'reschedule',
    component: () => import('@/views/portal/RescheduleView.vue'),
  },
  {
    // /booking/reschedule/calendar was the month-grid drilldown behind the
    // reschedule day-carousel's "vis hele måneden" link. #2012 replaced the
    // carousel with the inline month calendar, so the detour screen has
    // nothing left to show — redirect any live link (an old email, a
    // bookmark) back to the picker, which now IS the month view.
    path: '/booking/reschedule/calendar',
    redirect: (to) => ({ path: '/booking/reschedule', query: to.query }),
  },
  {
    path: '/booking/cancelled',
    name: 'cancelled',
    component: () => import('@/views/portal/CancelledView.vue'),
  },
  {
    // Reschedule success landing (#1267/#1296/#1300). The customer's
    // magic token is dead post-reschedule (single-use stamp, #1109)
    // so we can't navigate back to /booking?token=<old> — that GET
    // 410s. Render the confirmation here instead.
    path: '/booking/rescheduled',
    name: 'rescheduled',
    component: () => import('@/views/portal/RescheduledView.vue'),
  },
]

// Demo self-serve landing. When staff toggle the self-serve demo off,
// GET /api/public/demo-login 302-redirects to this tenant-context path
// (internal/admin/demo_login_handler.go, const demoRequestPath). A
// prospect leaves their name + email so a human can open the demo.
// Registered unconditionally so it resolves on the demo subdomain (the
// backend endpoint is fenced to the demo tenant). Public, no auth.
const demoRoutes: RouteRecordRaw[] = [
  {
    path: '/request-demo',
    name: 'request-demo',
    component: () => import('@/views/RequestDemoView.vue'),
  },
]

// Privacy magic-link landings. The URLs are baked into
// internal/notifications/dispatcher.go (gdpr.deletion / gdpr.access
// events) — keep them in sync if you ever rename these paths.
const privacyRoutes: RouteRecordRaw[] = [
  {
    path: '/privacy/my-data',
    name: 'privacy-my-data',
    component: () => import('@/views/privacy/MyDataView.vue'),
  },
  {
    path: '/privacy/confirm-delete',
    name: 'privacy-confirm-delete',
    component: () => import('@/views/privacy/ConfirmDeleteView.vue'),
  },
]

// Meeting
const meetingRoutes: RouteRecordRaw[] = [
  {
    path: '/join',
    name: 'lobby',
    component: () => import('@/views/meeting/LobbyView.vue'),
  },
  {
    // Distinct path from the invitee's /meeting (registered above in
    // portalRoutes). The two used to collide on /meeting — vue-router
    // matches the first-registered record for a path push, so callers
    // intending this Whereby-embed view silently landed on the
    // token-gated InviteeView ("Vi finner ikke dette møtet"). This view
    // is internal-only (reached by name from the dashboard + the
    // customer's manage page), so giving it its own path is safe: no
    // email or external link points here. Invitee email links keep
    // /meeting?token=.
    path: '/videorom',
    name: 'in-meeting',
    component: () => import('@/views/meeting/InMeetingView.vue'),
  },
  {
    path: '/after',
    name: 'after',
    component: () => import('@/views/meeting/AfterView.vue'),
  },
]

// Auth (tenant routes — magic-link login)
const authRoutes: RouteRecordRaw[] = [
  {
    path: '/auth/local-sign-out',
    name: 'auth-local-sign-out',
    component: () => import('@/views/auth/LocalSignOutView.vue'),
  },
  {
    path: '/auth/login',
    name: 'auth-login',
    component: () => import('@/views/auth/LoginView.vue'),
    // On apex (stemne.no), /auth/login has no tenant context to run
    // the WebAuthn ceremony against — RP ID = {slug}.stemne.no, but
    // there's no slug. Bounce to the marketing login (/login), which
    // does the email→tenant lookup and forwards to the right
    // subdomain. apiClient + stale bookmarks + share-links all
    // funnel through here, so the redirect catches every entry.
    //
    // Email query param is preserved so /login can prefill if it's
    // already typed (e.g. from a stale dashboard session bouncing).
    beforeEnter: (to) => {
      if (isMarketing) {
        return { name: 'login', query: to.query }
      }
      return true
    },
  },
  {
    path: '/auth/verify',
    name: 'auth-verify',
    component: () => import('@/views/auth/VerifyTokenView.vue'),
  },
  {
    path: '/auth/transfer',
    name: 'auth-transfer',
    component: () => import('@/views/auth/VerifyTokenView.vue'),
  },
  {
    path: '/auth/passkey',
    name: 'auth-passkey',
    component: () => import('@/views/auth/PasskeyEnrollView.vue'),
  },
  {
    // Trademark soft-reserve interstitial (#1063). Operators whose
    // signup tripped the bundled trademark-reserve list land here;
    // the screen polls /api/admin/me/review-status until staff
    // approve (redirects to /dashboard) or reject (shows reason +
    // contact CTA).
    path: '/auth/awaiting-review',
    name: 'auth-awaiting-review',
    component: () => import('@/views/auth/AwaitingReviewView.vue'),
  },
  {
    // Workspace-mismatch interstitial. The session's apex-shared
    // cookie belongs to a different org than this host serves —
    // AdminAuth 403s data calls with `wrong_workspace`, and both the
    // dashboard gates (checkTenantMismatch) and apiClient land here.
    // The operator chooses: continue to their own workspace, or log
    // out and log in on this one.
    path: '/auth/wrong-workspace',
    name: 'auth-wrong-workspace',
    component: () => import('@/views/auth/WrongWorkspaceView.vue'),
  },
  {
    // Gentle handoff before the legal document. The operator can still
    // explore the dashboard; this page explains why making a service visible
    // needs one extra step before showing the agreement itself.
    path: '/dpa-required',
    name: 'dpa-required',
    component: () => import('@/views/DpaRequiredView.vue'),
  },
  {
    // Universal databehandleravtale accept gate (#1887). The dashboard +
    // setup guards bounce any operator here (new or already-onboarded) when
    // operator.dpa-accept is on and the org hasn't accepted the current DPA.
    // Not under the /dashboard passkey gate — reachable on any authed factor
    // (incl. magic_link) so a blocked operator can always satisfy it, mirror
    // of the backend accept endpoint's auth-only mount.
    path: '/dpa-accept',
    name: 'dpa-accept',
    component: () => import('@/views/DpaAcceptView.vue'),
  },
  {
    // Operator entry point per CLAUDE.md: "{slug}.stemne.no/admin".
    // The /dashboard route owns the auth gate (redirects to
    // /auth/login when unauthed, /setup/* when onboarding), so the
    // documented URL just points there. Pre-#1298 a bare /admin fell
    // through to the catch-all 404, which showed magic-link-customer
    // copy to an operator typing /admin or following an old bookmark.
    path: '/admin',
    redirect: '/dashboard',
  },
  {
    // Operator invitation accept. URL emitted by POST
    // /api/admin/invitations is always
    // https://<slug>.stemne.no/admin/invite/<token> (see
    // tenant.HostedURL on the backend) — even for tenants with custom
    // frontends, since they typically don't implement this route.
    path: '/admin/invite/:token',
    name: 'admin-invite',
    component: () => import('@/views/admin/InviteAcceptView.vue'),
  },
  {
    // Email-change confirmation landing. URL emitted by the
    // notifications dispatcher when an operator requests an email
    // change (#137 second migration). Token rides in the query
    // string, not the path, because the email composer builds it
    // as `<base>/admin/confirm-email?token=<raw>`.
    path: '/admin/confirm-email',
    name: 'admin-confirm-email',
    component: () => import('@/views/admin/ConfirmEmailView.vue'),
  },
]

// Operator onboarding (tenant routes — on {slug}.stemne.no)
const setupRoutes: RouteRecordRaw[] = [
  {
    path: '/setup/profile',
    name: 'setup-profile',
    component: () => import('@/views/setup/SetupProfileView.vue'),
  },
  {
    // Optional company step (#13). Sits between profile and services so
    // the operator establishes the legal entity before listing what they
    // sell. Skippable — the form writes nothing on skip, and the state
    // machine accepts every membership without enforcing forward order.
    path: '/setup/company',
    name: 'setup-company',
    component: () => import('@/views/setup/SetupCompanyView.vue'),
  },
  {
    path: '/setup/services',
    name: 'setup-services',
    component: () => import('@/views/setup/SetupServicesView.vue'),
  },
  {
    path: '/setup/availability',
    name: 'setup-availability',
    component: () => import('@/views/setup/SetupAvailabilityView.vue'),
  },
  {
    path: '/setup/calendar',
    name: 'setup-calendar',
    component: () => import('@/views/setup/SetupCalendarView.vue'),
  },
  {
    path: '/setup/done',
    name: 'setup-done',
    component: () => import('@/views/setup/SetupDoneView.vue'),
  },
  {
    // Subscription paywall interstitial. The router's subscription gate
    // (applyDashboardGates*) bounces a frozen operator here — status in
    // trial_expired / past_due / canceled / paused — before the
    // dashboard paints. Deliberately a SIBLING of /dashboard, not a
    // child, so it renders standalone without the dashboard chrome
    // (mirrors /auth/awaiting-review). The billing tab under
    // /dashboard/settings stays reachable so the owner can recover;
    // everything else in /dashboard/* redirects here. See
    // subscriptionGate.ts + SUBSCRIPTION_INTERSTITIAL_PATH.
    path: '/abonnement',
    name: 'subscription-required',
    component: () => import('@/views/setup/SubscriptionRequiredView.vue'),
  },
  {
    path: '/dashboard',
    component: () => import('@/views/setup/DashboardView.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/setup/DashboardOverviewView.vue'),
      },
      {
        path: 'meetings',
        name: 'dashboard-meetings',
        component: () => import('@/views/setup/DashboardMeetingsView.vue'),
      },
      {
        // Full historikk — org-wide activity feed ("Alt som skjer hos
        // dere"). The view itself handles the dashboard.activity-feed
        // flag being off (the backing endpoint 404s and the page shows
        // an under-utrulling empty state), so the route stays
        // registered and a staff-flipped flag surfaces the page
        // without an SPA rebuild.
        path: 'historikk',
        name: 'dashboard-history',
        component: () => import('@/views/setup/DashboardHistoryView.vue'),
      },
      {
        // Innsikt — operator analytics dashboard (#442). The view
        // handles the analytics.dashboard flag being off (the backing
        // endpoint 404s and the page shows an under-utrulling state),
        // so the route stays registered and a staff-flipped flag
        // surfaces the page without an SPA rebuild. Ops-tier: the
        // backend route is RequireRole(owner, admin).
        path: 'innsikt',
        name: 'dashboard-analytics',
        component: () => import('@/views/setup/DashboardInnsiktView.vue'),
        meta: { requiresOps: true },
      },
      {
        // Rapporter — fakturagrunnlag (#2743). Same shape as Innsikt
        // above: the view handles the reports.invoice-basis flag being
        // off (the backing endpoint 404s and the page shows an
        // under-utrulling state), so the route stays registered and a
        // staff-flipped flag surfaces the page without an SPA rebuild.
        // Ops-tier: the backend route is RequireRole(owner, admin).
        path: 'rapporter',
        name: 'dashboard-reports',
        component: () => import('@/views/setup/DashboardRapporterView.vue'),
        meta: { requiresOps: true },
      },
      {
        // Avganger — group departures (stories/09). The view shows an
        // unavailable state while booking.departures is off. The optional
        // :departureId deep-links straight to one departure's roster.
        path: 'avganger/:departureId?',
        name: 'dashboard-departures',
        component: () => import('@/views/setup/DashboardDeparturesView.vue'),
      },
      {
        path: 'payments/disputes',
        name: 'dashboard-payment-disputes',
        component: () => import('@/views/setup/DashboardPaymentDisputesView.vue'),
        meta: { requiresOps: true },
      },
      {
        path: 'payments/disputes/:disputeId',
        name: 'dashboard-payment-dispute',
        component: () => import('@/views/setup/DashboardPaymentDisputeView.vue'),
        props: true,
        meta: { requiresOps: true },
      },
      {
        path: 'meetings/:bookingId',
        name: 'dashboard-meeting-detail',
        component: () => import('@/views/setup/DashboardBookingDetailView.vue'),
        props: true,
      },
      {
        // Payment bells briefly shipped this legacy URL. Keep the redirect so
        // already-persisted notifications open the meeting instead of the 404
        // page; new notifications write /dashboard/meetings/:bookingId.
        path: 'bookings/:bookingId',
        redirect: (to) => ({
          name: 'dashboard-meeting-detail',
          params: { bookingId: to.params.bookingId },
        }),
      },
      {
        // The action-point reminder email's one-tap Done / Snooze deeplink
        // target (S3a, #2130). :action is 'done' | 'snooze'; the view fires the
        // authenticated POST and shows the outcome.
        path: 'oppfolging/:id/:action',
        name: 'action-reminder-confirm',
        component: () => import('@/views/setup/ActionReminderConfirmView.vue'),
      },
      {
        // The live meeting surface. Route param matches the prep page so
        // deep-links work and the back button returns to the prep page
        // naturally.
        //
        // The component is a switch: meetings.live-redesign picks between the
        // redesigned møtevisning (a shim over the shell-owned overlay) and
        // the original stacked live view. See DashboardMeetingLiveSwitch.
        path: 'meetings/:id/live',
        name: 'dashboard-meeting-live',
        component: () => import('@/views/setup/DashboardMeetingLiveSwitch.vue'),
        props: true,
        // A cancelled / expired meeting is not happening, so its live
        // workspace must not render for someone who arrives at this URL
        // directly (stale link, bookmark, back button after a cancellation).
        // Sits on the route, above the live-redesign switch, so it covers
        // both surfaces AND runs before the overlay session opens. Fails
        // open — see liveMeetingGuard.
        beforeEnter: liveMeetingGuard,
      },
      {
        // Note detail view (#34). Routes Notater hub items into the
        // two-column detail surface; access_kind from the backend
        // picks the visual variant (full meeting context vs.
        // "Skjult av eier" placeholder for entry-scope recipients).
        path: 'notes/:entryId',
        name: 'dashboard-note-detail',
        component: () => import('@/views/setup/DashboardNoteDetailView.vue'),
        props: true,
      },
      {
        // #631: old Norwegian path. Worker-minted links (and emails in
        // flight) still point at /dashboard/notater/{id} until the
        // worker is redeployed with the new link builder — redirect
        // until that batch has cycled through.
        path: 'notater/:entryId',
        redirect: (to) => ({
          name: 'dashboard-note-detail',
          params: { entryId: to.params.entryId },
        }),
      },
      {
        // Legacy redirect — the worker (and emails in flight) still
        // mint /dashboard/notat/{id} links. Stay live until the next
        // notification batch cycles through after the worker is
        // redeployed; cleanup task #39 drops this.
        path: 'notat/:entryId',
        redirect: (to) => ({
          name: 'dashboard-note-detail',
          params: { entryId: to.params.entryId },
        }),
      },
      {
        path: 'services',
        name: 'dashboard-services',
        component: () => import('@/views/setup/DashboardServicesView.vue'),
      },
      {
        // /dashboard/availability merged into /dashboard/account as
        // the "Tilgjengelighet" tab. The redirect preserves any query
        // params (?addBlock=1, ?override=…) so help-centre deep links
        // continue to fire the right modal once the tab mounts.
        path: 'availability',
        name: 'dashboard-availability',
        redirect: (to) => ({
          name: 'dashboard-account',
          query: { ...to.query, account: 'tilgjengelighet' },
        }),
      },
      {
        path: 'calendar',
        name: 'dashboard-calendar',
        component: () => import('@/views/setup/DashboardCalendarView.vue'),
      },
      {
        // Manage view for the connected calendar: status, disconnect.
        // Calendar-picker UI (which calendars to sync, primary write
        // target) lands here in a follow-up — backend endpoints + the
        // CalendarList.list integration aren't shipped yet.
        path: 'calendar/manage',
        name: 'dashboard-calendar-manage',
        component: () => import('@/views/setup/DashboardCalendarManageView.vue'),
      },
      {
        path: 'customers',
        name: 'dashboard-customers',
        component: () => import('@/views/setup/DashboardCustomersView.vue'),
      },
      {
        // One customer's detail surface. Path uses the customers(id)
        // UUID (migration 091 made it the system of record). Email
        // stays in the response for display but isn't the identity.
        //
        // The V2 pill-filtered timeline (#1482) — the only customer-
        // detail surface as of #1699 (the legacy /history-backed flat
        // view and its flag were removed).
        path: 'customers/:id',
        name: 'dashboard-customer-detail',
        component: () => import('@/views/setup/DashboardCustomerDetailV2View.vue'),
        props: true,
      },
      {
        // Full-screen samtaleflate (messaging.thread-view). The
        // samtaler pill on the customer detail page routes here when
        // the flag is on; with the flag off the pill keeps filtering
        // the inline timeline and nothing links here. Route stays
        // registered regardless so a staff-flipped flag surfaces the
        // page without an SPA rebuild. meta.immersive drops the
        // mobile tab bar — the whole screen is conversation, the
        // header's back chevron is the way out.
        path: 'customers/:id/samtale',
        name: 'dashboard-customer-conversation',
        component: () => import('@/views/setup/DashboardCustomerConversationView.vue'),
        props: true,
        meta: { immersive: true },
      },
      {
        path: 'account',
        name: 'dashboard-account',
        component: () => import('@/views/setup/DashboardAccountView.vue'),
      },
      {
        // Operator help-centre — TOC + per-article. Gated by the
        // same dashboard auth chain (passkey factor required) so
        // articles can deep-link "click here to invite" buttons
        // straight to /dashboard/settings without a login dance.
        path: 'help',
        name: 'dashboard-help',
        component: () => import('@/views/setup/DashboardHelpIndexView.vue'),
      },
      {
        path: 'help/:slug',
        name: 'dashboard-help-article',
        component: () => import('@/views/setup/DashboardHelpArticleView.vue'),
        props: true,
      },
      {
        // Document-sharing surface (S1 placeholder). The view itself
        // checks the documents.sharing feature flag and renders the
        // empty-state stub until later slices wire the archive, room,
        // and manage UIs. Route stays registered so a staff-flipped
        // flag immediately surfaces the page without an SPA rebuild.
        // See docs/document-sharing-plan.md.
        path: 'documents',
        name: 'dashboard-documents',
        component: () => import('@/views/setup/DashboardDocumentsView.vue'),
      },
      {
        // Workspace-level settings — invitations, team management,
        // company config, security policies. Backend already gates
        // every sensitive endpoint with mw.RequireRole("admin") /
        // RequireRole("owner") (post owner-role split); this meta
        // flag is the matching SPA-side gate so advisors never see
        // the page in the first place. Owner-only tabs within the
        // page (billing, brand) are hidden via canManageBilling /
        // canManageBrand inside the view. See router guard below.
        path: 'settings',
        name: 'dashboard-settings',
        component: () => import('@/views/setup/DashboardSettingsView.vue'),
        meta: { requiresOps: true },
      },
      {
        // Public integration API token management (#447). Backend mounts
        // the matching admin endpoints under FlagPublicAPI; this route
        // is reachable independently of the flag — the view itself
        // renders an empty state when the flag is off (matches the
        // backend's 404 behaviour for a curl to /api/v1/*), so a
        // bookmark to /dashboard/integrations doesn't 500 on a
        // post-downgrade revisit. Sidebar entry is hidden via the
        // feature gate in DashboardView.
        //
        // `alias: 'integrasjoner'` keeps the old Norwegian URL working
        // for stale bookmarks. Drop after a release cycle.
        path: 'integrations',
        alias: 'integrasjoner',
        name: 'dashboard-integrations',
        component: () => import('@/views/setup/DashboardIntegrationsView.vue'),
        meta: { requiresOps: true },
      },
    ],
  },
]

// Edge cases
//
// Naming note: `/404` here is semantically "magic-link expired", not a real
// 404 — ManageView + RescheduleView redirect to it on token failure. A
// rename to `/expired` (and updating the 4 call sites) is queued as a
// follow-up refactor; deferred because the path is user-visible and breaks
// any operator who bookmarked an old expired-link landing. The real
// catch-all 404 lives at the bottom of this file.
const edgeRoutes: RouteRecordRaw[] = [
  {
    path: '/404',
    name: 'expired',
    component: () => import('@/views/edges/ExpiredView.vue'),
  },
  {
    path: '/off-by-consultant',
    name: 'off-by-consultant',
    component: () => import('@/views/edges/OffByConsultantView.vue'),
  },
  {
    path: '/fully-booked',
    name: 'fully-booked',
    component: () => import('@/views/edges/FullyBookedView.vue'),
  },
  {
    path: '/403',
    name: 'error-forbidden',
    component: () => import('@/views/edges/ErrorPageView.vue'),
    meta: { errorCode: '403' },
  },
  {
    path: '/410',
    name: 'error-gone',
    component: () => import('@/views/edges/ErrorPageView.vue'),
    meta: { errorCode: '410' },
  },
  {
    path: '/500',
    name: 'error-server',
    component: () => import('@/views/edges/ErrorPageView.vue'),
    meta: { errorCode: '500' },
  },
]

// Catch-all 404. Must be appended last so it loses every other match. The
// pathMatch param is exposed as `route.params.pathMatch` if a handler ever
// needs the original URL — ErrorPageView reads `route.fullPath` directly so
// it doesn't matter here.
const notFoundRoute: RouteRecordRaw = {
  path: '/:pathMatch(.*)*',
  name: 'error-not-found',
  component: () => import('@/views/edges/ErrorPageView.vue'),
  meta: { errorCode: '404' },
}

// Marketing-only routes (stemne.no — no tenant). Mixed indexability:
// /pricing + /get-started are part of the public marketing surface
// and live in the sitemap; /login, /verify, /get-started/subdomain are
// auth-gated or mid-funnel and carry noindex per #526.
const marketingRoutes: RouteRecordRaw[] = isMarketing
  ? [
      {
        path: '/for/behandlere',
        name: 'practitioners',
        component: () => import('@/views/marketing/PractitionersView.vue'),
        meta: landingRouteMeta('/for/behandlere'),
      },
      {
        path: '/login',
        name: 'login',
        component: () => import('@/views/marketing/LoginView.vue'),
        meta: { noindex: true },
      },
      {
        // Redirect to the landing page's complete, Stripe-live pricing
        // section until a dedicated pricing page exists. A routed
        // placeholder here was publicly indexable (UX review 2026-06).
        path: '/pricing',
        name: 'pricing',
        redirect: { path: '/', hash: '#pricing' },
      },
      {
        path: '/get-started',
        name: 'sign-up',
        component: () => import('@/views/marketing/SignUpView.vue'),
      },
      {
        path: '/verify',
        name: 'verify',
        component: () => import('@/views/marketing/VerifyView.vue'),
        meta: { noindex: true },
      },
      {
        // Apex "find my bookings" — an end customer who forgot which
        // operator(s) they booked with enters their email and gets one
        // email listing each operator with a per-operator portal link.
        // Apex-only (no tenant context); the backend endpoint is gated
        // by the portal.cross-operator-find flag, and the view degrades
        // to a neutral "not available" message on a 404 when off. noindex —
        // it's a utility entry point, not marketing content.
        path: '/finn-avtaler',
        name: 'find-bookings',
        component: () => import('@/views/FinnAvtalerView.vue'),
        meta: { noindex: true },
      },
      {
        path: '/get-started/subdomain',
        name: 'subdomain',
        component: () => import('@/views/marketing/SubdomainView.vue'),
        meta: { noindex: true },
      },
      {
        // Public Swagger UI for the v1 integration API. Apex-only and
        // unauthenticated — mirrors the OpenAPI spec at
        // /api/v1/openapi.yaml, which is itself served without auth.
        // Rendered standalone (standard Swagger theme, no dashboard
        // chrome). Indexable: this is the public API reference.
        path: '/swagger/v1',
        name: 'api-docs-swagger',
        component: () => import('@/views/marketing/SwaggerDocsView.vue'),
      },
    ]
  : []

// Stemne staff console — cross-tenant management. Lives at /staff on
// any host: in prod the parent cookie domain lets stemne.no/staff
// see kodelauget.stemne.no's auth cookie; in dev cookies are
// host-only so we register the routes on the tenant context too.
// Server-side /api/staff/* routes are outside SubdomainResolver so
// they don't care about host either.
//
// /staff/orgs/:id is its own page (not a modal over the list) — the
// detail surface is too dense for a modal, and confirmation modals
// stack much more cleanly over a full page.
const staffRoutes: RouteRecordRaw[] = [
  {
    // Oversikt is the landing tab (#707). /staff used to be the org list;
    // that moved to /staff/orgs and KEPT the `staff-orgs` route name, so
    // every router.push({ name: 'staff-orgs' }) call site is unaffected.
    path: '/staff',
    name: 'staff-oversikt',
    component: () => import('@/views/marketing/StaffOversiktView.vue'),
    meta: { requiresStaff: true },
  },
  {
    path: '/staff/orgs',
    name: 'staff-orgs',
    component: () => import('@/views/marketing/StaffOrgsView.vue'),
    meta: { requiresStaff: true },
  },
  {
    path: '/staff/orgs/:id',
    name: 'staff-org-detail',
    component: () => import('@/views/marketing/StaffOrgDetailView.vue'),
    meta: { requiresStaff: true },
  },
  {
    path: '/staff/flags',
    name: 'staff-flags',
    component: () => import('@/views/marketing/StaffFlagsView.vue'),
    meta: { requiresStaff: true },
  },
  {
    path: '/staff/flags/:key',
    name: 'staff-flag-detail',
    component: () => import('@/views/marketing/StaffFlagDetailView.vue'),
    meta: { requiresStaff: true },
  },
  {
    path: '/staff/signups',
    name: 'staff-signups',
    component: () => import('@/views/marketing/StaffSignupsReportView.vue'),
    meta: { requiresStaff: true },
  },
  {
    // Annonser (in-app ad-builder) retired — the repo `pnpm render-ads`
    // pipeline owns ad creative now, and the daily routine carries the UTM.
    // Redirect the old path so bookmarks land on Dagens plan.
    path: '/staff/ads',
    name: 'staff-ads',
    redirect: { name: 'staff-social' },
  },
  {
    path: '/staff/social',
    name: 'staff-social',
    component: () => import('@/views/marketing/StaffSocialView.vue'),
    meta: { requiresStaff: true },
  },
  {
    // Ring folded into Marketing (/staff/social) as a tab. Keep the old path
    // working with a redirect so bookmarks land on the Ring tab.
    path: '/staff/ring',
    name: 'staff-ring',
    redirect: { name: 'staff-social', query: { tab: 'ring' } },
  },
  {
    // Meldinger folded into "Dagens plan" (/staff/social) as a tab. Keep the
    // old path working with a redirect so bookmarks and email links don't 404.
    path: '/staff/outreach',
    name: 'staff-outreach',
    redirect: { name: 'staff-social' },
  },
  {
    path: '/staff/security',
    name: 'staff-security',
    component: () => import('@/views/marketing/StaffSecurityView.vue'),
    meta: { requiresStaff: true },
  },
  {
    // Personvern (#2578): Stemne's DPIA — the interactive, editable risk
    // matrix + risk cards. Staff-only, Stemne-internal.
    path: '/staff/personvern',
    name: 'staff-compliance',
    component: () => import('@/views/marketing/StaffComplianceView.vue'),
    meta: { requiresStaff: true },
  },
  {
    // Maskin-tokens (Fase 0): mint/list/revoke the headless bearer
    // credentials the external social-draft agent uses.
    path: '/staff/machine-tokens',
    name: 'staff-machine-tokens',
    component: () => import('@/views/marketing/StaffMachineTokensView.vue'),
    meta: { requiresStaff: true },
  },
  {
    // Sentry feedback-widget review. Deep-linked from the #alerts Slack
    // ping; shows one submission and the button that opens a GitHub
    // issue for it. No tab in StaffShell — it is a link target, not a
    // section, same as /staff/orgs/:id.
    path: '/staff/feedback/:issueId',
    name: 'staff-feedback-review',
    component: () => import('@/views/marketing/StaffFeedbackReviewView.vue'),
    meta: { requiresStaff: true },
  },
  {
    // Trademark soft-reserve review queue (#1063). Lists orgs in
    // review_state='awaiting_review' with approve/reject controls.
    path: '/staff/orgs/pending',
    name: 'staff-orgs-pending',
    component: () => import('@/views/marketing/StaffPendingReviewView.vue'),
    meta: { requiresStaff: true },
  },
]

// noindexed tags every route in a group with `meta.noindex = true`.
// The router's afterEach hook reads this flag and toggles a
// <meta name="robots" content="noindex,nofollow"> tag on the document
// head. Cheaper than editing each route block by hand, and the call
// site documents intent: "this whole group is private."
//
// We mutate in place. The route record objects are constructed inline
// in this module and not shared with anything else; mutation keeps
// the RouteRecordRaw discriminated-union type intact (a spread widens
// it and TypeScript rejects the assignment). Child routes inherit
// parent meta automatically via Vue Router's meta merge, so we don't
// need to recurse.
//
// Mirrors the Disallow list in functions/robots.txt.ts. The robots.txt
// hint saves Google's crawl budget; the meta tag is the actual hard
// block. Keep both in sync when adding a new private path prefix.
function noindexed(group: RouteRecordRaw[]): RouteRecordRaw[] {
  for (const r of group) {
    r.meta = { ...r.meta, noindex: true }
  }
  return group
}

// Tag the catch-all 404 with noindex separately. notFoundRoute is a
// const declared earlier (single record, not in a group); inlining the
// mutation would type-narrow against the existing `meta` shape, so the
// helper keeps the call site in routes[] tidy.
function notFoundRouteWithNoIndex(): RouteRecordRaw {
  notFoundRoute.meta = { ...notFoundRoute.meta, noindex: true }
  return notFoundRoute
}

const routes: RouteRecordRaw[] = [
  ...bookingRoutes, // public — marketing landings + tenant booking flow
  ...angrerettRoutes, // tenant-only — the statutory withdrawal form
  ...noindexed(demoRoutes),
  ...noindexed(portalRoutes),
  ...noindexed(privacyRoutes), // magic-link landings; the /privacy + /terms marketing pages live in bookingRoutes
  ...noindexed(meetingRoutes),
  ...noindexed(authRoutes),
  ...noindexed(setupRoutes),
  ...noindexed(edgeRoutes),
  ...marketingRoutes, // mixed — entries flag themselves
  ...noindexed(staffRoutes),
  notFoundRouteWithNoIndex(),
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Top on new navigations, saved position on back/forward, and hash
  // navigations (/#pricing) resolve to the anchor element — with a poll
  // for cold loads where the view mounts after the router settles.
  // See scrollBehavior.ts for the full rationale.
  scrollBehavior: hashScrollBehavior,
})

// /dashboard/* gate decisions, factored so we can run them both
// synchronously (cache hit → block-and-decide before paint) and
// asynchronously (cache miss → let the route paint with skeletons,
// then router.replace if a gate fails).
//
// applyDashboardGatesSync returns undefined to allow nav, or a
// redirect (string / { path } / false) matching Vue Router's
// beforeEach contract. The cross-tenant guard calls
// window.location.replace because Vue Router can't navigate across
// origins (hostname change).
type DashboardGateRedirect = string | { path: string } | false

function applyDashboardGatesSync(
  to: RouteLocationNormalized,
  me: NonNullable<ReturnType<typeof useCurrentAdmin>['current']['value']>,
): DashboardGateRedirect | undefined {
  // Workspace mismatch FIRST: "this session belongs to another org"
  // trumps every per-session gate below. A magic_link session on the
  // wrong host must land on the interstitial, not get bounced to
  // /auth/passkey — passkey enrollment belongs to the workspace the
  // session is for, and register/begin 403s under mismatch anyway.
  if (me.authOrgSlug) {
    const decision = checkTenantMismatch({
      hostname: window.location.host,
      platformHost: PLATFORM_HOST,
      authOrgSlug: me.authOrgSlug,
    })
    if (decision.kind === 'block') {
      // Interstitial, not a silent teleport: a multi-workspace
      // operator visiting their second workspace wants "log out and
      // log in here", which a hard redirect made unreachable. The
      // view offers both actions and carries the intended path.
      return `/auth/wrong-workspace?next=${encodeURIComponent(to.fullPath)}`
    }
  }

  const bounce = shouldRequirePasskey(to.fullPath, me.factor ?? null, me.hasPasskey ?? false)
  if (bounce) return bounce

  // Operatøravtale gate (#3025 P1). Same rule, same screen, its own status:
  // the two agreements version independently, so an org that has accepted the
  // DPA can still owe this one. Evaluated right after the DPA gate and before
  // the setup/subscription gates so the ordering the operator experiences is
  // unchanged — passkey first, then the one agreements screen. Both bounces
  // resolve to the same path, so when both are outstanding the operator lands
  // there once and the screen renders both documents.
  const termsBounce = shouldBounceToTermsAccept(to.path, useTermsGate().gateActive())
  if (termsBounce) return { path: termsBounce }

  // Onboarding-step gate: only evaluated when loadServerStep has
  // already populated. Cold-load takes the async path, which awaits it.
  const { serverStep, loaded: serverStepLoaded } = useOnboardingResume()
  if (serverStepLoaded.value) {
    const setupBounce = shouldBounceDashboardToSetup(to.path, serverStep.value)
    if (setupBounce) return { path: setupBounce }
  }

  // Subscription gate: a frozen operator (trial_expired / past_due /
  // canceled / paused) is bounced to the /abonnement paywall before the
  // dashboard paints. Placed AFTER the onboarding gate — a mid-setup
  // operator is still `trialing` (never frozen), and finishing setup
  // takes priority. The billing tab under /dashboard/settings is exempt
  // so the owner can recover. SPA mirror of RequireActiveSubscription;
  // the backend still 402s writes as the real enforcement.
  const subBounce = shouldBlockForSubscription(to.path, me.subscriptionStatus)
  if (subBounce) return subBounce

  return undefined
}

// Resolves /me + the onboarding-step probe in parallel, then applies
// every gate against the resolved identity. Called fire-and-forget
// from the dashboard guard on cold load; it issues its own
// router.replace / window.location.replace when a gate fails. Guards
// against stale redirects when the operator has already navigated
// elsewhere by the time /me lands.
async function applyDashboardGatesAsync(to: RouteLocationNormalized): Promise<void> {
  const { fetchCurrentAdmin } = useCurrentAdmin()
  const { loadServerStep, serverStep, loaded: serverStepLoaded } = useOnboardingResume()
  const { loadTermsStatus, loaded: termsLoaded, gateActive: termsGateActive } = useTermsGate()
  // The DPA gate is the one gate below that reads a feature flag
  // (operator.dpa-accept, via peek). Awaiting the gate STATUS without also
  // awaiting the flag store left peek() false on a cold load, so the gate
  // failed open and the operator landed on a dashboard where every data call
  // 403s dpa_required — scattered "ingen tilgang" fragments instead of the
  // blocking accept screen. load() is session-cached and shares one in-flight
  // fetch with App.vue's boot load, so this costs no extra request whichever
  // of the two gets there first.
  const { load: loadFeatures } = useFeatures()

  const [me] = await Promise.all([
    fetchCurrentAdmin(),
    loadFeatures(),
    serverStepLoaded.value ? Promise.resolve() : loadServerStep(),
    termsLoaded.value ? Promise.resolve() : loadTermsStatus(),
  ])

  // Stale-redirect guard: operator may have already SPA-navigated to
  // a different route while /me was in flight. Don't yank them off the
  // new page based on a gate decision for an old destination. Auth
  // failures still bounce when the user is anywhere in /dashboard —
  // no subtree of /dashboard should be reachable without a session.
  const stillOnTarget = router.currentRoute.value.fullPath === to.fullPath
  const stillOnDashboard = router.currentRoute.value.path.startsWith('/dashboard')

  if (!me) {
    if (stillOnDashboard) {
      const here = router.currentRoute.value.fullPath
      router.replace(`/auth/login?next=${encodeURIComponent(here)}`)
    }
    return
  }

  if (!stillOnTarget) return

  // Workspace mismatch FIRST — same ordering rationale as the sync
  // gate above.
  if (me.authOrgSlug) {
    const decision = checkTenantMismatch({
      hostname: window.location.host,
      platformHost: PLATFORM_HOST,
      authOrgSlug: me.authOrgSlug,
    })
    if (decision.kind === 'block') {
      router.replace(`/auth/wrong-workspace?next=${encodeURIComponent(to.fullPath)}`)
      return
    }
  }

  const bounce = shouldRequirePasskey(to.fullPath, me.factor ?? null, me.hasPasskey ?? false)
  if (bounce) {
    router.replace(bounce)
    return
  }

  // Operatøravtale gate (cold-load path). Mirrors the sync branch. loadFeatures
  // above is awaited for this gate too — gateActive() reads
  // peek('operator.terms-accept'), and the same missed await is what made the
  // DPA gate fail open on a cold load.
  const termsBounce = shouldBounceToTermsAccept(to.path, termsGateActive())
  if (termsBounce) {
    router.replace({ path: termsBounce })
    return
  }

  const setupBounce = shouldBounceDashboardToSetup(to.path, serverStep.value)
  if (setupBounce) {
    router.replace({ path: setupBounce })
    return
  }

  // Subscription gate (cold-load path). Mirrors the sync branch above:
  // a frozen operator is bounced to /abonnement once /me resolves.
  const subBounce = shouldBlockForSubscription(to.path, me.subscriptionStatus)
  if (subBounce) {
    router.replace(subBounce)
  }
}

// Clear the global-search page context on every navigation so one route's
// "på denne siden" cards + on-view boost never leak onto the next page. Each
// dashboard view re-adds its own via useSearchContext on mount. Registered
// before the async auth guard; it only mutates module state and never blocks.
router.beforeEach(() => {
  resetSearchContext()
})

// Role guard: routes flagged `meta.requiresOps` are reachable to
// operators with the ops tier (owner OR admin per the post-owner-role
// taxonomy). Advisors get bounced to /dashboard. The backend already
// returns 403 on every sensitive endpoint behind mw.RequireRole; this
// is the matching SPA gate so advisors don't even see the page.
// fetchCurrentAdmin is module-cached so the per-navigation cost is
// one map lookup.
router.beforeEach(async (to, from) => {
  // #1499/#1503 — per-navigation re-check of the tenant-not-found
  // flag. App.vue's onMounted bounces to error-not-found on initial
  // boot, but the check runs ONCE. When the operator clicks
  // "Tilbake til forsiden" from the 404 page and SPA-navigates to
  // `/`, the booking shell mounts with a null tenant (empty h1 +
  // "Ingen tjenester er tilgjengelige") because nothing re-validates
  // the flag. This guard catches every push/replace.
  const { notFound: tenantNotFound } = useTenant()
  const tenantRedirect = resolveTenantNotFoundRedirect(to, tenantNotFound.value)
  if (tenantRedirect) return tenantRedirect

  // Query-only navigation within the same path — calendar swipes
  // (?view=&date=), list filters, tab params. Auth factor, onboarding
  // step, role and tenant binding cannot change because a query
  // param did, so the network-backed gates below (fresh /me round-
  // trip + onboarding-step probe) are skipped. Without this, every
  // calendar swipe waited on /me before the view repainted — the
  // gesture visibly lagged the data. from.matched is empty only on
  // the initial navigation, which must always run the full gate.
  const queryOnlyNavigation = from.matched.length > 0 && to.path === from.path

  // Auto-logout when an ALREADY-ONBOARDED operator navigates back into
  // the signup flow (e.g. they have a stale session from a previous
  // signup and try /get-started/email again). Without this, the
  // SubdomainView reads the prior org's slug from /api/admin/me and
  // pre-fills, the user types a new slug, the backend tries to RENAME
  // the existing org and rejects.
  //
  // Crucially we DO NOT logout for in-progress signups — a user who
  // just verified their email and is on /get-started/subdomain has a
  // legitimate magic_link session that the flow needs to complete the
  // claim. Only onboarding_step === 'done' indicates "this session
  // belongs to a finished operator, not a fresh signup attempt".
  const isSignupRoute =
    to.path === '/get-started' || to.path.startsWith('/get-started/') || to.path === '/verify'
  if (isSignupRoute && !queryOnlyNavigation) {
    try {
      // api.probe (not api.get): these run on signup routes for a possibly-
      // stale session — a 401/non-ok must mean "no finished session, proceed
      // with signup", never a hard-redirect to /auth/login or an identity-
      // cache flush. probe shares the client's slug + credentials + API_BASE
      // plumbing without those side effects.
      const stepRes = await api.probe('/api/admin/onboarding-step')
      if (stepRes.ok) {
        const step = (await stepRes.json<{ step?: string }>()) ?? {}
        // Only the 'done' state means the session belongs to a fully
        // onboarded operator. Any in-progress step (subdomain, profile,
        // services, availability, calendar) is a legitimate continuation.
        if (step.step === 'done') {
          const meRes = await api.probe('/api/admin/me')
          if (meRes.ok) {
            const me = (await meRes.json<{ csrf_token?: string }>()) ?? {}
            // Explicit logout is excluded from the client's 401-retry +
            // CSRF-bootstrap, so thread the CSRF token from /me
            // explicitly since probe() doesn't capture it into the client's
            // in-memory store. Swallow any error — we hard-navigate regardless.
            await endOperatorSession({
              headers: me.csrf_token ? { 'X-CSRF-Token': me.csrf_token } : {},
            })
            window.location.href = to.fullPath
            return false
          }
        }
      }
    } catch {
      // Network failure on the probe — proceed. We don't want a
      // transient blip to block signup; the worst case is the user
      // sees the pre-population bug and re-submits.
    }
  }

  // /setup/* gate. Same auth-presence pre-check as /dashboard/* below
  // — an un-authed visit to /setup/profile (e.g. browser bookmark
  // pointed at the wizard step from a previous attempt, or a refresh
  // after the cookies got cleared) was rendering the wizard chrome
  // with empty state because loadServerStep swallows 401s. Redirect
  // to /auth/login first so the wizard never paints without a session.
  //
  // Then for in-progress steps (everything except /setup/done) the
  // existing rule (shouldBounceSetupRoute) decides whether the
  // operator is still allowed on this wizard step. /setup/done is
  // reachable to operators on their way out (transitional view) so
  // it's exempt from the step gate but NOT from the auth gate.
  if (to.path.startsWith('/setup/') && !queryOnlyNavigation) {
    const { fetchCurrentAdmin, current } = useCurrentAdmin()
    const me = await fetchCurrentAdmin()
    // A failed /me is not the same thing as a revoked session, and this
    // guard used to treat them identically — see shouldBounceSetupToLogin
    // for why that logged out operators who were still signed in. Same
    // "refresh the cache, don't bounce on null" posture the /dashboard/*
    // gate below already takes.
    if (shouldBounceSetupToLogin(me, current?.value)) {
      return `/auth/login?next=${encodeURIComponent(to.fullPath)}`
    }
    // No DPA gate here on purpose (#1887): the setup wizard runs
    // unblocked and the accept screen comes AFTER passkey enrollment, on
    // the way into the dashboard — one blocking screen at a time. The
    // dashboard guard runs shouldRequirePasskey before
    // The DPA is enforced contextually at publication and customer-data
    // creation, rather than blocking dashboard exploration. The operatøravtale gate
    // (#3025 P1) is exempt here for the same reason and lands on the same
    // screen — the wizard is not gated on either agreement.
    if (to.path !== '/setup/done') {
      const { loadServerStep, serverStep } = useOnboardingResume()
      await loadServerStep()
      const bounce = shouldBounceSetupRoute(to.path, serverStep.value)
      if (bounce) return { path: bounce }
    }
  }

  // /dashboard/* passkey-factor gate. SPA mirror of the backend's
  // RequirePasskeyFactor middleware. Without this, a fresh signup
  // landing on test1337.qa.stemne.no/dashboard via direct nav (post-
  // DNS refresh) sees the dashboard chrome render while every API
  // call 403s, and has to log out + back in to be prompted for
  // passkey enrolment. The helper is unit-tested in
  // composables/__tests__/shouldRequirePasskey.spec.ts.
  if ((to.path === '/dashboard' || to.path.startsWith('/dashboard/')) && !queryOnlyNavigation) {
    const { current, fetchCurrentAdmin } = useCurrentAdmin()
    const cached = current?.value ?? null

    // Steady state (cached identity): gates run synchronously against
    // the cache. Background-revalidate /me only when stale (5-min TTL);
    // refresh the cache, don't bounce on null. Mid-session revocation
    // is caught by apiClient's 401 interceptor on the next data call;
    // factor/role flips land on the next navigation once the cache
    // updates. A transient network blip during the background fetch
    // must not log out a working session.
    if (cached) {
      if (isCurrentAdminStale()) void fetchCurrentAdmin()
      const sync = applyDashboardGatesSync(to, cached)
      if (sync !== undefined) return sync
      return
    }

    // Cold load (cache empty: hard refresh, URL-bar nav, or post-
    // invalidate — including the post-passkey-enroll router.push to
    // /dashboard, which invalidates the identity cache first).
    //
    // Block ONLY on the cheap onboarding-step probe so a mid-onboarding
    // operator is bounced to /setup/* BEFORE the dashboard paints.
    // Without this the bounce rode the fire-and-forget
    // applyDashboardGatesAsync below: the dashboard mounted, fired its
    // /bookings + /services + /me loads, then yanked itself to /setup/*
    // — a visible flash + wasted fetches for an operator who hasn't
    // finished setup. loadServerStep is session-cached (one fetch per
    // tab) and fail-open (a probe failure leaves serverStep null →
    // shouldBounceDashboardToSetup returns null → proceed), so this
    // can't trap a legitimate operator behind a flaky probe.
    const { loadServerStep, serverStep } = useOnboardingResume()
    await loadServerStep()
    const setupBounce = shouldBounceDashboardToSetup(to.path, serverStep.value)
    if (setupBounce) return { path: setupBounce }

    // /me still resolves in the background — DON'T block paint on it
    // (the deliberate cold-load design). If the session is invalid, two
    // safety nets fire:
    //   1. applyDashboardGatesAsync resolves /me, then router.replace
    //      to /auth/login (or whatever gate failed).
    //   2. apiClient's 401 interceptor hard-redirects on the first
    //      authenticated data call from any mounted view, which beats
    //      our promise on a slow /me.
    // Both end in /auth/login?next=<original path>; the user notices
    // a moment of skeleton instead of a network wait.
    void applyDashboardGatesAsync(to)
  }
  if (to.meta?.requiresOps && !queryOnlyNavigation) {
    // Cache-first (same rationale as the /dashboard gate above): every
    // requiresOps route is a /dashboard child, so the cache is already
    // warm from that gate's fetch. Use it for the role check and only
    // hit the network on a cold cache — role is stable within a session
    // (a role change forces re-login), so this can't go stale silently.
    const { current, fetchCurrentAdmin } = useCurrentAdmin()
    const me = current?.value ?? (await fetchCurrentAdmin())
    // Unauthenticated → let downstream auth flow handle it (apiClient
    // redirects on 401). Authenticated but wrong role → bounce home.
    if (me && !isOps(me.role)) return { name: 'dashboard' }
    return true
  }
  if (to.meta?.requiresStaff) {
    // Apex bounce — production AND QA. The staff passkey ceremony is
    // scoped to the platform apex (RP ID = PLATFORM_HOST), and the
    // session cookie with Domain=PLATFORM_HOST crosses subdomains,
    // so a tenant-subdomain staff link bounces to the apex cleanly.
    //
    // Pre-fix this hardcoded 'stemne.no', so on QA every
    // {slug}.qa.stemne.no/staff was bounced to prod stemne.no/staff
    // — and qa.stemne.no/staff was also bounced because the host !==
    // 'stemne.no' check passed there too. Going through isMarketingHost
    // + PLATFORM_HOST handles all envs uniformly.
    //
    // Dev does NOT bounce: cookies are host-only in dev (COOKIE_DOMAIN
    // is empty per /CLAUDE.md and #825), so a bounce from
    // kodelauget.stemne.test:5173 → stemne.test:5173 would land the
    // user without a JWT and the guard would kick them home. The dev
    // backend skips the sudo + passkey-factor middlewares on staff
    // routes (see cmd/api/routes_platform.go), so the console works
    // from any tenant subdomain in dev.
    //
    // Port-presence is the dev signal — prod/QA have no explicit
    // port; mkcert dev on *.stemne.test:5173 (and legacy
    // *.localhost:5173) always do. Pre-fix this used a `localhost`
    // string match which silently broke under the *.stemne.test
    // mkcert apex.
    const host = window.location.hostname
    const isDev = window.location.port !== ''
    if (!isDev && host.endsWith(PLATFORM_HOST) && !isMarketingHost(host)) {
      window.location.replace(`https://${PLATFORM_HOST}${to.fullPath}`)
      return false
    }

    // Staff guard pings /api/staff/me. Three outcomes worth
    // distinguishing:
    //   200 — staff, proceed
    //   401 — no auth at all, bounce to /login with ?next=/staff so the
    //         post-auth redirect lands them back here instead of root.
    //         (apex marketing context only — on tenant subdomains the
    //         apex bounce above already happened, so by here we're
    //         either at stemne.no or in dev.)
    //   403 — authenticated but not in stemne_staff. Silent bounce to
    //         `/` so we don't leak that /staff exists for non-staff
    //         operators. Same shape as the existing behaviour.
    //   network error — treat as 403 (silent), same reason.
    try {
      // api.probe shares the client's slug-header + credentials + API_BASE
      // plumbing but, unlike api.get, does NOT 401-refresh-retry or
      // hard-redirect via window.location — the guard needs to branch on the
      // status itself and return a Vue Router redirect. (The prior relative
      // fetch('/api/staff/me') hit the SPA origin, which 405s from Cloudflare
      // Pages in prod where the API lives on api.stemne.no; routing through
      // the client's API_BASE fixes that.)
      const res = await api.probe('/api/staff/me')
      if (res.ok) return true
      if (res.status === 401) {
        // ?next is recorded for future round-trip support — today the
        // marketing /login → tenant /auth/login → passkey flow doesn't
        // thread it back to apex, so the user lands on /dashboard
        // post-auth and re-navigates to /staff. Better than the prior
        // silent bounce to / with no signpost.
        return { name: 'login', query: { next: to.fullPath } }
      }
      return { path: '/' }
    } catch {
      return { path: '/' }
    }
  }
  return true
})

// Toggle the <meta name="robots"> tag per navigation (#526). Private
// routes are tagged with meta.noindex = true via the noindexed() helper
// or per-entry meta blocks; the hook syncs the document head so each
// SPA navigation lands with the right indexing hint.
//
// JS-disabled crawlers won't see this tag (it's injected client-side),
// but the robots.txt Disallow list in functions/robots.txt.ts is the
// belt-and-braces hard block for those crawlers. The two together cover
// every realistic indexing path.
const applyRouteMetadata = createRouteMetadataUpdater()
router.afterEach((to) => {
  applyRobotsMeta(Boolean(to.meta?.noindex))
  applyCanonical(to.path)
  applyRouteMetadata({ title: to.meta.title, description: to.meta.description })
})

// Recover from a lazy route chunk that fails to load (#730, #3874). A deploy
// retires old content-hashed chunks, and Cloudflare Pages answers a missing
// /assets/*.js with the SPA index HTML under a 4-hour cache header, so the
// browser can hold a poisoned copy that a plain reload keeps serving from
// cache. The handler re-fetches the page's asset URLs with `cache: 'reload'`
// BEFORE reloading (that is what overwrites the poisoned entries); if the page
// still fails within the guard window it stops and App.vue shows a load-error
// panel instead of a blank page. See utils/chunkRecovery.ts.
//
// Wrapped rather than passed directly: onError's listener receives `from` as
// its third argument, which must not land in the handler's `deps` slot.
router.onError((err, to) => {
  void handleChunkLoadError(err, to)
})

// A later successful navigation (e.g. browser back) clears the error panel and
// the reload guard.
router.afterEach((_to, _from, failure) => {
  if (!failure) clearChunkRecoveryState()
})

// Re-exported so router/__tests__/isDynamicImportError.spec.ts keeps importing
// the predicate from here.
export { isDynamicImportError } from '@/utils/chunkRecovery'

export default router
